'use client'

import { Info } from 'lucide-react'

import { Tooltip, TooltipContent, TooltipTrigger } from '@ui/web/components'
import { typographyVariants } from '@ui/web/lib'
import { formatDate } from '@helper/utils/format'
import cn from '@config/tailwind/cn'

import type { Student } from '../types'
import { getStudentAccess } from '../types'

/**
 * `DD/MM/YY`, zero-padded.
 *
 * Not `formatDate`: its `dateStyle: 'short'` leaves id-ID free to drop the leading zero, so a
 * column of dates would not align down the roster. Built from parts for that reason alone.
 */
function formatSeatExpiry(value: Date | string): string | null {
  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) return null

  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const year = String(date.getFullYear()).slice(-2)

  return `${day}/${month}/${year}`
}

interface AccessCellProps {
  student: Student
  /** Which side is reading. Decides how the seat's provenance is named — see `getStudentAccess`. */
  type?: 'web' | 'school'
}

/**
 * Access level, with the seat's expiry under it and everything else behind a tooltip.
 *
 * The column answers ONE question at a glance — Premium or Terbatas — and a roster row is dense
 * enough that a second and third line of qualifiers made every row a paragraph. So the cell shows
 * the level, and for a Premium seat the date it lapses; who pays for it, a seat bought for next
 * term, and a renewal already placed are facts an admin wants occasionally and about one student
 * at a time, which is what a tooltip is for.
 *
 * The info icon appears only when there is something to say. A row with no extra detail carries no
 * affordance at all, so the icon's presence is itself the signal that this student is a special
 * case.
 */
export function AccessCell({ student, type }: AccessCellProps) {
  const access = getStudentAccess(student, type)

  const expiry = access.expiresAt ? formatSeatExpiry(access.expiresAt) : null

  /**
   * The qualifiers, in the order an admin asks for them.
   *
   * A family roster drops the provenance: on `web` the reader is the parent, and "who is paying for
   * this child" is a school admin's question, not theirs — naming the payer there only invites
   * "Keluarga" on a row the parent knows is theirs already.
   */
  const details: string[] = []

  if (type !== 'web' && access.sourceLabel) details.push(`Akses dari: ${access.sourceLabel}`)

  // A seat already bought for this student that has not opened yet. The row reads Terbatas and
  // stays correct — the child cannot read premium today — but without this nothing distinguishes
  // them from a student nobody has paid for, and the seat gets bought twice.
  if (access.startsAt) details.push(`Premium mulai ${formatDate(access.startsAt, { dateStyle: 'long' })}`)

  // A renewal already bought for this student. The date beside the label is the renewed one, so
  // without this the row looks no different from a seat that simply runs that long.
  if (access.isRenewed) details.push('Sudah diperpanjang')

  return (
    <div className='srd:flex srd:flex-col srd:text-nowrap'>
      <span className='srd:flex srd:items-center srd:gap-x-1'>
        <span className={cn(typographyVariants({ variant: 'label-md' }), 'srd:text-neutral-black')}>
          {access.label}
        </span>

        {details.length > 0 && (
          <Tooltip>
            <TooltipTrigger type='button' aria-label={`Detail akses ${access.label}`} className='srd:shrink-0'>
              <Info className='srd:text-blue-100 srd:size-4' />
            </TooltipTrigger>
            <TooltipContent className='srd:max-w-[240px] srd:text-left'>
              <span className='srd:flex srd:flex-col srd:gap-y-0.5'>
                {details.map((detail) => (
                  <span key={detail}>{detail}</span>
                ))}
              </span>
            </TooltipContent>
          </Tooltip>
        )}
      </span>

      {expiry && (
        <span className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:text-neutral-black')}>{expiry}</span>
      )}
    </div>
  )
}
